import { Injectable } from '@angular/core';
import { OfficeRoute as OfficeStartRoute } from '@office-start/officehome-types';
import { OfficeThemeMode } from '@office-start/theming-service';
import { getRouteFlight, isVisualBackgroundEnabled } from '@office-start/visual-service';
import { VisualGates } from '@office-start/visual-types';
import { combineLatest, Observable } from 'rxjs';
import { map } from 'rxjs/operators';
import { OfficeRoute } from '../constants/office-route.enum';
import { CapabilitiesService } from '../shared-context/capabilities.service';
import { AuthType, ClientStartupConfigService } from '../shared-context/client-startup-config.service';
import { OfficeRouteService } from './office-route.service';
import { ThemingService } from './theming.service';

export interface VisualConfigEvent {
    visualBackgroundEnabled: boolean;
    visualOfficeWrapperStyle: { [key: string]: string };
}

@Injectable({
    providedIn: 'root'
})
export class VisualService {
    public visualConfigSubscription: Observable<VisualConfigEvent>;

    constructor(
        protected readonly capabilitiesService: CapabilitiesService,
        protected readonly clientStartupService: ClientStartupConfigService,
        protected readonly routeService: OfficeRouteService,
        protected readonly themingService: ThemingService
    ) {
        this.visualConfigSubscription = combineLatest(this.themingService.themeShades, this.themingService.darkModeSubscription, this.routeService.getRouteChangeEndObservable())
        .pipe(map(([themeEvent, darkModeEvent, routeEvent]) => {
            const themeId = ((themeEvent.theme.themeId.toLowerCase() === ThemingService.baseThemeId || themeEvent.theme.themeId === ThemingService.staticDarkTheme) && themeEvent.theme.reactShellTheme.PreferOfficeTheme) || themeEvent.theme.themeId === ThemingService.baseWithOfficeThemePreferredThemeId ? ThemingService.officeThemeId : themeEvent.theme.themeId;
            const visualBackgroundEnabled = isVisualBackgroundEnabled(
                this.clientStartupService.config.authType === AuthType.MSA,
                window.matchMedia('(-ms-high-contrast: active)').matches || window.matchMedia('(forced-colors: active)').matches,
                getRouteFlight(routeEvent.currentRoute.route as OfficeStartRoute),
                themeId,
                darkModeEvent ? OfficeThemeMode.dark : OfficeThemeMode.default,
                this.getVisualGates()
            );
            return {
                visualBackgroundEnabled,
                visualOfficeWrapperStyle: this.getVisualOfficeWrapperStyle(routeEvent.currentRoute.route)
            };
        }));
    }

    protected getVisualGates(): VisualGates {
        return {
            visualAppsModuleEnabled: this.capabilitiesService.capabilities.visualAppsModuleEnabled,
            visualBackgroundEnabled: this.capabilitiesService.capabilities.visualBackgroundEnabled,
            visualCreateCenterModuleEnabled: this.capabilitiesService.capabilities.visualCreateCenterModuleEnabled,
            visualEnabled: this.capabilitiesService.capabilities.visualEnabled,
            visualFeedModuleEnabled: this.capabilitiesService.capabilities.visualFeedModuleEnabled,
            visualMyContentModuleEnabled: this.capabilitiesService.capabilities.visualMyContentModuleEnabled,
            visualSettingEnabled: this.capabilitiesService.capabilities.visualSettingEnabled
        };
    }

    protected getVisualOfficeWrapperStyle(route: OfficeRoute): { [key: string]: string } {
        if (route === OfficeRoute.OfficeHome || route === OfficeRoute.Pwa || route === OfficeRoute.Create) {
            return {
                position: 'relative'
            };
        }
        return {};
    }
}
